import { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Servicio de API para autenticación
class AuthAPI {
  static async login(email, password) {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        error.message || 
        'Error de conexión con el servidor'
      );
    }
  }

  static async refreshToken(refreshToken) {
    try {
      const response = await axios.post('/api/auth/refresh', {
        refresh_token: refreshToken,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error renovando token'
      );
    }
  }

  static async getCurrentUser() {
    try {
      const response = await axios.get('/api/auth/me');
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error obteniendo información del usuario'
      );
    }
  }

  static async updateProfile(updates) {
    try {
      const response = await axios.put('/api/auth/me', updates);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error actualizando perfil'
      );
    }
  }

  static async changePassword(passwords) {
    try {
      const response = await axios.post('/api/auth/change-password', passwords);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error cambiando contraseña'
      );
    }
  }

  static async verifyToken(token) {
    try {
      const response = await axios.post('/api/auth/verify-token', {
        token,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error verificando token'
      );
    }
  }

  static async logout() {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.warn('Error en logout del servidor:', error);
    }
  }

  // Métodos administrativos
  static async getUsers(includeInactive = false, sucursalId = undefined) {
    try {
      const response = await axios.get('/api/auth/users', {
        params: { include_inactive: includeInactive, sucursal_id: sucursalId || undefined },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error obteniendo usuarios'
      );
    }
  }

  static async createUser(userData) {
    try {
      const response = await axios.post('/api/auth/users', userData);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error creando usuario'
      );
    }
  }

  static async updateUser(userId, updates) {
    try {
      const response = await axios.put(`/api/auth/users/${userId}`, updates);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error actualizando usuario'
      );
    }
  }

  static async deleteUser(userId) {
    try {
      const response = await axios.delete(`/api/auth/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 
        'Error eliminando usuario'
      );
    }
  }
}

const fetchClienteInfo = async () => {
  try {
    const res = await axios.get('/api/clientes/me');
    if (res.data.success && res.data.data) {
      localStorage.setItem('clienteInfo', JSON.stringify(res.data.data));
      return res.data.data;
    }
  } catch (e) {
    // 404 = usuario sin cliente asignado, no es error crítico
    if (e.response?.status !== 404) {
      console.warn('Error cargando info del cliente:', e.message);
    }
  }
  localStorage.removeItem('clienteInfo');
  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [clienteInfo, setClienteInfo] = useState(() => {
    try {
      const saved = localStorage.getItem('clienteInfo');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  // Parqueaderos asignados al usuario. `clientesDisponibles` es solo la
  // pantalla de selección obligatoria del login; esta lista se conserva para
  // poder cambiar de sucursal en cualquier momento, sin cerrar sesión.
  const [misParqueaderos, setMisParqueaderos] = useState(() => {
    try {
      const saved = localStorage.getItem('misParqueaderos');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  // Selección de sucursal obligatoria: rol sin modo global (operador/guardia)
  // con más de una sucursal asignada. Ya hay sesión (token válido) pero no
  // puede operar hasta elegir una de estas.
  const [sucursalesPendientes, setSucursalesPendientes] = useState([]);
  const [clienteEnEspera, setClienteEnEspera] = useState(null);

  const guardarParqueaderos = (lista) => {
    const parqueaderos = lista || [];
    setMisParqueaderos(parqueaderos);
    localStorage.setItem('misParqueaderos', JSON.stringify(parqueaderos));
  };

  // Función para limpiar sesión
  const clearSession = () => {
    setUser(null);
    setClienteInfo(null);
    setAccessToken(null);
    setRefreshToken(null);
    setClientesDisponibles([]);
    setMisParqueaderos([]);
    setSucursalesPendientes([]);
    setClienteEnEspera(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('clienteInfo');
    localStorage.removeItem('misParqueaderos');
  };

  // Cargar sesión guardada al iniciar
  useEffect(() => {
    const loadSavedSession = async () => {
      try {
        const savedAccessToken = localStorage.getItem('accessToken');
        const savedRefreshToken = localStorage.getItem('refreshToken');
        
        if (savedAccessToken && savedRefreshToken) {
          // Configurar el token en axios
          axios.defaults.headers.common['Authorization'] = `Bearer ${savedAccessToken}`;
          
          try {
            // Verificar si el token de acceso sigue siendo válido
            const userData = await AuthAPI.getCurrentUser();
            setUser(userData.user);
            setAccessToken(savedAccessToken);
            setRefreshToken(savedRefreshToken);
            if (userData.user?.cliente_id) {
              const ci = await fetchClienteInfo();
              setClienteInfo(ci);
            }
          } catch (error) {
            // Token de acceso expirado, intentar renovar
            try {
              const refreshData = await AuthAPI.refreshToken(savedRefreshToken);
              
              setUser(refreshData.user);
              setAccessToken(refreshData.access_token);
              setRefreshToken(savedRefreshToken);
              
              // Guardar nuevo token de acceso
              localStorage.setItem('accessToken', refreshData.access_token);
              axios.defaults.headers.common['Authorization'] = `Bearer ${refreshData.access_token}`;
            } catch (refreshError) {
              console.warn('Sesión expirada:', refreshError.message);
              clearSession();
            }
          }
        }
      } catch (error) {
        console.error('Error cargando sesión:', error);
        clearSession();
      } finally {
        setLoading(false);
      }
    };

    loadSavedSession();
  }, []);

  // Interceptor de respuesta para manejar tokens expirados automáticamente
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        const isRefreshEndpoint = originalRequest.url?.includes('/api/auth/refresh');
        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshEndpoint) {
          originalRequest._retry = true;

          try {
            const savedRefreshToken = localStorage.getItem('refreshToken');
            if (savedRefreshToken) {
              const refreshData = await AuthAPI.refreshToken(savedRefreshToken);

              setAccessToken(refreshData.access_token);
              localStorage.setItem('accessToken', refreshData.access_token);

              originalRequest.headers.Authorization = `Bearer ${refreshData.access_token}`;
              axios.defaults.headers.common['Authorization'] = `Bearer ${refreshData.access_token}`;

              return axios(originalRequest);
            }
          } catch (refreshError) {
            console.warn('Token expirado, cerrando sesión:', refreshError.message);
            clearSession();
          }
        }

        // Si el refresh falla con 401, cerrar sesión inmediatamente
        if (error.response?.status === 401 && isRefreshEndpoint) {
          clearSession();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Configurar renovación automática de tokens cada 10 minutos
  useEffect(() => {
    let refreshInterval;

    if (accessToken && refreshToken) {
      refreshInterval = setInterval(async () => {
        try {
          const refreshData = await AuthAPI.refreshToken(refreshToken);
          setAccessToken(refreshData.access_token);
          localStorage.setItem('accessToken', refreshData.access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${refreshData.access_token}`;
        } catch (error) {
          console.error('Error renovando token automáticamente:', error);
          logout();
        }
      }, 10 * 60 * 1000); // 10 minutos
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [accessToken, refreshToken]);

  const login = async (email, password) => {
    try {
      const response = await AuthAPI.login(email, password);

      if (response.success) {
        // Multi-parqueadero: el usuario debe elegir uno antes de continuar
        guardarParqueaderos(response.clientes);

        if (response.needs_selection && response.clientes?.length > 1) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`;
          setClientesDisponibles(response.clientes);
          return { success: true, needs_selection: true, clientes: response.clientes };
        }

        if (response.needs_seleccion_sucursal) {
          // Rol sin modo global con varias sucursales asignadas: hay sesión
          // (token válido) pero falta elegir una antes de poder operar.
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`;
          setAccessToken(response.access_token);
          setRefreshToken(response.refresh_token);
          localStorage.setItem('accessToken', response.access_token);
          localStorage.setItem('refreshToken', response.refresh_token);
          const clienteId = response.user?.cliente_id;
          setClienteEnEspera(clienteId);
          try {
            const mp = await axios.get('/api/auth/mis-parqueaderos');
            const cli = (mp.data?.data || []).find((c) => c.id === clienteId);
            setSucursalesPendientes(cli?.sucursales || []);
          } catch {
            setSucursalesPendientes([]);
          }
          return { success: true, needs_seleccion_sucursal: true };
        }

        setUser(response.user);
        setAccessToken(response.access_token);
        setRefreshToken(response.refresh_token);

        localStorage.setItem('accessToken', response.access_token);
        localStorage.setItem('refreshToken', response.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`;

        if (response.user?.cliente_id) {
          const ci = await fetchClienteInfo();
          setClienteInfo(ci);
        }

        return { success: true, user: response.user };
      } else {
        return { success: false, error: response.error || 'Error en el login' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // sucursalId: número = entra a esa sucursal; null = modo global explícito
  // (vuelve a ver todas); undefined/omitido = no elige, el backend resuelve
  // el default (global si el rol puede, o la única/las asignadas si no).
  const selectCliente = async (clienteId, sucursalId) => {
    try {
      const response = await axios.post('/api/auth/select-parqueadero', {
        cliente_id: clienteId,
        ...(sucursalId !== undefined ? { sucursal_id: sucursalId } : {}),
      });
      const data = response.data;

      if (data.success) {
        setUser(data.user);
        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;

        if (data.user?.cliente_id) {
          const ci = await fetchClienteInfo();
          setClienteInfo(ci);
        }

        setClientesDisponibles([]);
        setSucursalesPendientes([]);
        setClienteEnEspera(null);
        return { success: true, needs_seleccion_sucursal: !!data.needs_seleccion_sucursal };
      }
      return { success: false, error: data.error };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  // Elegir la sucursal pendiente tras un login que quedó a medias
  // (needs_seleccion_sucursal). clienteEnEspera ya quedó guardado en login().
  const selectSucursal = (sucursalId) => selectCliente(clienteEnEspera, sucursalId);

  const cancelSucursalSelection = () => {
    setSucursalesPendientes([]);
    setClienteEnEspera(null);
    logout();
  };

  const cancelClienteSelection = () => {
    setClientesDisponibles([]);
    delete axios.defaults.headers.common['Authorization'];
  };

  const logout = async () => {
    try {
      // Intentar notificar al servidor sobre el logout
      await AuthAPI.logout();
    } catch (error) {
      console.warn('Error notificando logout al servidor:', error);
    } finally {
      // Limpiar axios headers
      delete axios.defaults.headers.common['Authorization'];
      clearSession();
    }
  };

  const updateUserProfile = async (updates) => {
    if (!accessToken) {
      throw new Error('No hay sesión activa');
    }

    try {
      const response = await AuthAPI.updateProfile(updates);
      
      if (response.success) {
        setUser(response.user);
        return { success: true, user: response.user };
      } else {
        return { success: false, error: response.error || 'Error actualizando perfil' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    if (!accessToken) {
      throw new Error('No hay sesión activa');
    }

    try {
      const response = await AuthAPI.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      if (response.success) {
        return { success: true, message: response.message };
      } else {
        return { success: false, error: response.error || 'Error cambiando contraseña' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const isAuthenticated = () => {
    return user !== null && accessToken !== null;
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  // Métodos administrativos
  const getUsers = async (includeInactive = false, sucursalId = undefined) => {
    try {
      const response = await AuthAPI.getUsers(includeInactive, sucursalId);
      return response.success ? response : { success: false, error: 'Error obteniendo usuarios' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createUser = async (userData) => {
    try {
      const response = await AuthAPI.createUser(userData);
      return response.success ? response : { success: false, error: 'Error creando usuario' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      const response = await AuthAPI.updateUser(userId, updates);
      return response.success ? response : { success: false, error: 'Error actualizando usuario' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await AuthAPI.deleteUser(userId);
      return response.success ? response : { success: false, error: 'Error eliminando usuario' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    clienteInfo,
    accessToken,
    refreshToken,
    loading,
    login,
    logout,
    selectCliente,
    misParqueaderos,
    cambiarParqueadero: selectCliente,
    cancelClienteSelection,
    clientesDisponibles,
    sucursalesPendientes,
    needsSucursalSelection: sucursalesPendientes.length > 0 && clienteEnEspera != null,
    selectSucursal,
    cancelSucursalSelection,
    updateUserProfile,
    changePassword,
    isAuthenticated,
    hasRole,
    hasAnyRole,
    // Métodos administrativos
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    // Instancia de axios configurada
    axios,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};